import { fromEvent, merge } from 'rxjs';
import { distinctUntilKeyChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import { isFocusable, isTabbable } from 'tabbable';
import { Disposable } from '..';
import { USER_SET_FOCUS } from '../../types';
import { FTV_OUTLINE } from '../../ui/standard/theme/constants';
import { FOCUS_NEXT_ELEMENT, FOCUS_PREVIOUS_ELEMENT } from '../shortcut/types';

export default class AccessibilityController extends Disposable {
  constructor(player) {
    super();
    this.player = player;

    this.navigation$ = AccessibilityController.setupNavigationStream(
      player.shortcutController.shortcuts$,
      player.focused$
    );

    AccessibilityController.createForceFocusOnElementStream(
      this.player.userEvents$
    ).subscribe(AccessibilityController.handleElementToFocus);

    AccessibilityController
      .outline(player.shortcutController.shortcuts$)
      .subscribe(({ event, outline }) => {
        const { target } = event;
        // Outline on wrapper is handled with a className because of extra cases with autoHide
        if (target && target.contains(document.querySelector('.magneto-wrapper'))) return;
        // eslint-disable-next-line no-param-reassign
        target.style.boxShadow = outline ? FTV_OUTLINE : 'unset';
        // eslint-disable-next-line no-param-reassign
        target.style.borderRadius = outline ? '2px' : '0px';
      });
  }

  static handleElementToFocus({ element }) {
    return !!element && isTabbable(element) && isFocusable(element) && element?.focus();
  }

  static isTabKey(keyCode) {
    return (keyCode === FOCUS_NEXT_ELEMENT || keyCode === FOCUS_PREVIOUS_ELEMENT);
  }

  static setupNavigationStream(keyDown$, focused$) {
    return merge(
      keyDown$.pipe(
        filter((keyCode) => AccessibilityController.isTabKey(keyCode)),
        map(() => true)
      ),
      focused$.pipe(filter(Boolean))
    );
  }

  static outline(shortcuts$) {
    return merge(
      AccessibilityController.createMagnetoBlurStream(),
      AccessibilityController.createMagnetoFocusStream(shortcuts$)
    ).pipe(distinctUntilKeyChanged('outline'));
  }

  static createMagnetoBlurStream() {
    const magnetoWrapper = document.getElementsByClassName('magneto-wrapper');
    return fromEvent(magnetoWrapper, 'blur').pipe(
      map((event) => ({ event, outline: false }))
    );
  }

  static createMagnetoFocusStream(shortcuts$) {
    const magnetoWrapper = document.getElementsByClassName('magneto-wrapper');
    return fromEvent(magnetoWrapper, 'focus')
      .pipe(
        withLatestFrom(shortcuts$),
        filter(([, keyCode]) => AccessibilityController.isTabKey(keyCode)),
        map(([event]) => ({ event, outline: true }))
      );
  }

  static createForceFocusOnElementStream(userEvents$) {
    return userEvents$.pipe(
      filter(({ action }) => action === USER_SET_FOCUS),
      map(({ payload }) => payload)
    );
  }
}
